import {
  Badge,
  ColorSwatch,
  Icon,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  VisualPicker,
  Box,
  NO_COLOR,
  PICKER_COLORS,
  toColor,
  type Color,
  type NoColor,
  type Omittable,
} from "@salestrak/ui";
import { formatNameList } from "#app/lib/utils/format-name-list";
import type { CustomFieldDef } from "./types";

// ---------------------------------------------------------------------------
// Select/labels option color + chip vocabulary. An option's `color` is a
// curated palette name persisted on custom_field_option. Multi Select values
// render SOFT (the 15% wash under the hue's ink); Single Select renders TONAL
// (the hue as a deep or pale surface under the same ink) — one categorical
// value, deliberately heavier than a chip pile, and legible on every hue in
// both modes where white-on-fill was not. The "no color" sentinel is no
// longer OFFERED (the field-level showOptionColors toggle is how a field goes
// quiet) but stored "none" values still render as the neutral outline chip; a
// null/unknown stored color coerces to grey.
// ---------------------------------------------------------------------------

export type OptionColorChoice = Color | NoColor;

/** Coerce a stored option color: "none" passes through, a valid palette name
 *  narrows, anything else (null, legacy junk) reads grey. */
export function asOptionColor(stored: string | null | undefined): OptionColorChoice {
  if (stored === NO_COLOR) return NO_COLOR;
  return toColor(stored) ?? "grey";
}

/** Random palette color for a fresh option (the draft row / inline create),
 *  excluding the previous option's color so consecutive adds never repeat a
 *  swatch. Never returns the "none" sentinel — colorless is manual-only. */
export function randomOptionColor(exclude?: string | null): Color {
  const pool = PICKER_COLORS.filter((c) => c !== exclude);
  const pick = pool[Math.floor(Math.random() * pool.length)];
  return pick ?? "grey";
}

export interface OptionMeta {
  value: string;
  label: string;
  color: OptionColorChoice;
}

/** Resolve a field's option list to render-ready metas (in stored order). */
export function getOptionMetas(field: CustomFieldDef): OptionMeta[] {
  return (field.config?.options ?? []).flatMap((opt) => {
    const value = opt?.value ?? opt?.label ?? "";
    if (!value) return [];
    return [
      {
        value,
        label: opt?.label ?? value,
        color: asOptionColor(opt?.color),
      },
    ];
  });
}

/** Whether a field's VALUE chips wear each option's color (absent means
 *  true). Off, every value chip renders as the neutral outline chip — the
 *  field stops competing for attention without losing its option grammar.
 *  The colors stay stored per option, so the switch is reversible and the
 *  option picker (where options are managed and recolored) keeps showing them. */
export function showsOptionColors(field: CustomFieldDef): boolean {
  return field.config?.showOptionColors !== false;
}

/** The color a VALUE chip of `field` renders for an option. Display only —
 *  never feed this back into an option write, or the stored color is lost. */
export function displayOptionColor(
  field: CustomFieldDef,
  color: OptionColorChoice,
): OptionColorChoice {
  return showsOptionColors(field) ? color : NO_COLOR;
}

/** Selected values → DISPLAY option metas, keeping stored-but-deleted values
 *  visible (they render as the grey fallback chip rather than vanishing).
 *  Colors are the field's display colors (`displayOptionColor`), so this is
 *  not a source for an option write — `getOptionMetas` is. */
export function resolveSelectedOptions(
  field: CustomFieldDef,
  values: unknown,
): OptionMeta[] {
  const selected = Array.isArray(values) ? values.map(String) : [];
  const byValue = new Map(getOptionMetas(field).map((o) => [o.value, o]));
  return selected.map((value) => {
    const meta = byValue.get(value) ?? { value, label: value, color: "grey" as const };
    return { ...meta, color: displayOptionColor(field, meta.color) };
  });
}

// ---------------------------------------------------------------------------
// OptionChip — one option value as a chip. `tone` follows the type: labels
// values are soft, a select value is tonal. The "no color" choice drops the
// fill for a neutral outline chip at identical metrics.
// ---------------------------------------------------------------------------

export function OptionChip({
  label,
  color,
  tone = "soft",
  caret,
  onDismiss,
  shrink,
}: Omittable<{
  label: string;
  color: OptionColorChoice;
  tone?: "soft" | "tonal";
  /** In-fill dropdown caret — the chip doubles as the picker trigger. */
  caret?: boolean;
  /** Trailing × removing this value (the picker's token field). */
  onDismiss?: () => void;
  /** Let the chip compress inside a tight row (a FitRow cluster): the label
   *  truncates deeper instead of the chip overflowing its cell. */
  shrink?: boolean;
}>) {
  const isNone = color === NO_COLOR;
  return (
    <Badge
      color={isNone ? undefined : color}
      tone={isNone ? "outline" : tone}
      size="tag"
      className={shrink ? "max-w-40 min-w-0 shrink" : "max-w-40"}
      onDismiss={onDismiss}
      dismissLabel={onDismiss ? `Remove ${label}` : undefined}
    >
      {/* The chip's size sets the type (14px regular on the value rung) —
          the label inherits rather than restating a size. */}
      <Text size={null} truncate>
        {label}
      </Text>
      {caret ? <Icon name="chevron-down" size="xs" /> : null}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// OptionValueText — a field with its option colors turned OFF reads its values
// as plain text wherever they rest (cells, property rows, card tokens): one
// comma-joined line that overflows the way text does everywhere else — an
// ellipsis, with the whole list in a tooltip once it clips. No counter: a
// count after truncated prose says less than the tooltip does. The picker
// keeps the chips — it is where options are managed and recolored, so the
// colors stay visible there.
// ---------------------------------------------------------------------------

export function OptionValueText({
  options,
  caret,
}: Omittable<{
  options: OptionMeta[];
  /** Trailing dropdown caret — the read-out doubles as the picker trigger. */
  caret?: boolean;
}>) {
  return (
    <Box direction="row" align="center" gap="xs" minWidth={0}>
      <Text size="sm" truncate overflowTooltip>
        {options.map((o) => o.label).join(", ")}
      </Text>
      {caret ? <Icon name="chevron-down" size="xs" color="muted" /> : null}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// OptionOverflowChip — the "+N" at the end of a chip cluster, reading the
// hidden options out as one sentence on hover ("Water, Hail, and 2 others"),
// the entity-group "+N" grammar. The count alone is the fact the cluster
// shows; the names are what the reader hovers for.
// ---------------------------------------------------------------------------

export function OptionOverflowChip({
  names,
}: Omittable<{
  /** The hidden options' labels, in cluster order. */
  names: string[];
}>) {
  return (
    <Tooltip>
      <TooltipTrigger render={<Badge size="tag" />}>{`+${names.length}`}</TooltipTrigger>
      <TooltipPopup>
        <Box maxWidth={320}>{formatNameList(names)}</Box>
      </TooltipPopup>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// OptionColorGlyphButton — the 24px swatch pressable that anchors the color
// popup (VisualPicker, color section only). At rest it shows the option's
// swatch, or the circle-slash glyph for a legacy stored "no color" value.
// ---------------------------------------------------------------------------

export function OptionColorGlyphButton({
  color,
  onColorChange,
  onOpenChange,
  "aria-label": ariaLabel,
}: {
  color: OptionColorChoice;
  onColorChange: (color: OptionColorChoice) => void;
  /** Popup open-state report — hosts with blur-commit rows guard on it. */
  onOpenChange?: (open: boolean) => void;
  "aria-label": string;
}) {
  return (
    <VisualPicker
      // A legacy "none" value has no swatch to mark; grey stands in until a
      // pick replaces it.
      color={color === NO_COLOR ? "grey" : color}
      onColorChange={onColorChange}
      onOpenChange={onOpenChange}
      tooltip="Set color"
      trigger={
        <Box
          as="button"
          type="button"
          width={24}
          height={24}
          align="center"
          justify="center"
          rounded="sm"
          hoverBackground
          aria-label={ariaLabel}
        >
          {color === NO_COLOR ? (
            <Icon name="circle-slash" size="sm" color="muted" />
          ) : (
            <ColorSwatch color={color} size="sm" rounded="full" />
          )}
        </Box>
      }
    />
  );
}
